import { useEffect } from "react";
import PageHero from "@/components/PageHero";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Privacy Policy
 *
 * Minimal, honest policy that reflects what the site actually does today:
 *   • collects name + phone/email + message via four internal contact forms
 *   • stores submissions in our database for the admin team
 *   • optional public user accounts (email/password or Google) for future features
 *   • no third-party analytics, no ad networks, no third-party trackers
 *
 * Deliberately short. No legalese-for-the-sake-of-legalese.
 */

const C = {
  ar: {
    eyebrow: "الخصوصية",
    title: "سياسة الخصوصية",
    lead: "نلتزم في مجتمع الذكاء الاصطناعي السوري (SAS) بحماية خصوصيّة كل من يزور موقعنا أو يتواصل معنا. توضّح هذه السياسة ما نجمعه من بيانات ولماذا، وما لا نجمعه على الإطلاق.",
    updatedLabel: "آخر تحديث",
    updatedDate: "نيسان/أبريل 2026",
    sections: [
      {
        heading: "البيانات التي نجمعها",
        body: [
          "عندما تُرسل طلباً عبر نماذج التواصل الأربعة (الشراكة، استضافة ورشة، تواصل عام، انضمام كمتطوّع)، نجمع البيانات التي تُدخلها طوعاً فقط: الاسم الكامل، رقم الهاتف أو البريد الإلكتروني، نصّ الطلب، وطريقة الرد المفضّلة.",
          "نحتفظ تلقائياً ببيانات تقنية بسيطة مع كل طلب: عنوان IP المُختصر ومعلومات المتصفّح. نستخدمها حصراً لحماية الموقع من الإرسال الآلي والتحقّق من الاستخدام العادل.",
          "إذا أنشأت حساب مستخدم عام على الموقع، نحتفظ بالاسم والبريد الإلكتروني الذي قدّمته، مع كلمة مرور مشفّرة (hash) لا يمكن عكسها.",
        ],
      },
      {
        heading: "كيف نستخدم بياناتك",
        body: [
          "نستخدم البيانات التي تُقدّمها في نماذج التواصل لغرض واحد فقط: الردّ على طلبك والتنسيق معك. لا نُرسل لك رسائل تسويقية ولا نضعك في قوائم بريدية دون إذنك.",
          "لا نبيع بياناتك، ولا نُشاركها مع أطراف ثالثة لأغراض إعلانية. ولا نستخدم أي شبكات إعلانية أو تتبّع عبر المواقع.",
        ],
      },
      {
        heading: "ما لا نستخدمه",
        body: [
          "لا يستخدم الموقع خدمات Google Analytics أو أي نظام تحليلات خارجي يجمع بيانات المستخدمين.",
          "لا نستخدم ملفّات تعريف ارتباط (cookies) إلا لأغراض تشغيلية أساسيّة (مثل إبقاء المشرف مسجّلاً في لوحة التحكّم).",
          "لا نُرسل بيانات الزوّار إلى أي طرف ثالث لأغراض الإعلانات أو التسويق.",
        ],
      },
      {
        heading: "الحفظ والحذف",
        body: [
          "نحتفظ ببيانات طلبات التواصل ما دامت مفيدة للمتابعة ومراجعة الأثر، ثم تُحذف بشكل دوري.",
          "يمكنك في أي وقت طلب حذف بيانات طلب أرسلته، أو حذف حسابك العام إن وُجد، بإرسال طلب عبر نموذج «التواصل العام» مع توضيح ما تريد حذفه.",
        ],
      },
      {
        heading: "الأمان",
        body: [
          "تُخزَّن البيانات على خوادم مشفّرة عبر بروتوكول HTTPS أثناء النقل، وكلمات المرور تُخزَّن بصيغة مشفّرة (bcrypt). لا يطّلع على طلبات التواصل سوى فريق الإدارة المحدود في SAS.",
        ],
      },
      {
        heading: "تعديلات على هذه السياسة",
        body: [
          "قد نُحدّث هذه الصفحة من وقت لآخر. يظهر تاريخ آخر تحديث أعلى الصفحة. أيّ تعديل جوهري يُعلَن بشكل واضح.",
        ],
      },
    ],
    contactEyebrow: "للتواصل حول الخصوصية",
    contactBody: "لأي سؤال أو طلب يتعلّق بهذه السياسة أو ببيانات حسابك، يُرجى استخدام ",
    contactLink: "نموذج التواصل العام",
    contactAfter: " مع الإشارة إلى «الخصوصية» في الموضوع.",
    docTitle: "سياسة الخصوصية · مجتمع الذكاء الاصطناعي السوري",
  },
  en: {
    eyebrow: "Privacy",
    title: "Privacy Policy",
    lead: "At the Syrian AI Society (SAS) we are committed to protecting the privacy of everyone who visits our site or contacts us. This page explains what we do and do not collect, and why.",
    updatedLabel: "Last updated",
    updatedDate: "April 2026",
    sections: [
      {
        heading: "What we collect",
        body: [
          "When you submit a request through one of our four contact forms (Partnership, Host a Workshop, General Contact, Join as a Volunteer), we only collect what you voluntarily enter: your full name, phone number or email, the text of your request, and your preferred reply method.",
          "Along with each submission we automatically keep a small amount of technical metadata — a shortened IP address and basic browser information — used exclusively to protect the site against automated spam and to enforce fair use.",
          "If you create a public user account, we keep the name and email you provided, along with a securely hashed (irreversible) password.",
        ],
      },
      {
        heading: "How we use your data",
        body: [
          "We use the data you enter in our forms for a single purpose: to respond to your request and coordinate with you. We do not send marketing emails and do not add you to mailing lists without your explicit consent.",
          "We do not sell your data, and we do not share it with third parties for advertising. We do not run any ad networks or cross-site tracking.",
        ],
      },
      {
        heading: "What we do not use",
        body: [
          "The site does not use Google Analytics or any third-party analytics product that profiles visitors.",
          "We only use cookies for essential operational purposes (for example keeping an admin signed in to the dashboard).",
          "We do not send visitor data to any third party for advertising or marketing.",
        ],
      },
      {
        heading: "Retention & deletion",
        body: [
          "We keep contact-form submissions as long as they remain useful for follow-up and impact review, and then delete them periodically.",
          "You may request deletion of a request you submitted, or of your public user account, at any time by sending a message through the General Contact form with a note referring to “privacy / deletion.”",
        ],
      },
      {
        heading: "Security",
        body: [
          "Data is stored on HTTPS-secured infrastructure; passwords are stored using bcrypt hashing. Contact-form submissions are accessible only to the small SAS admin team.",
        ],
      },
      {
        heading: "Changes to this policy",
        body: [
          "We may update this page from time to time. The last-updated date above reflects the most recent revision. Any material change will be clearly flagged.",
        ],
      },
    ],
    contactEyebrow: "Privacy inquiries",
    contactBody: "For any question about this policy or your account data, please use our ",
    contactLink: "General Contact form",
    contactAfter: " and mention “Privacy” in the subject line.",
    docTitle: "Privacy Policy · Syrian AI Society",
  },
};

export default function Privacy() {
  const { lang } = useLanguage();
  const c = C[lang];

  useEffect(() => {
    const prev = document.title;
    document.title = c.docTitle;
    return () => { document.title = prev; };
  }, [c.docTitle]);

  return (
    <>
      <PageHero eyebrow={c.eyebrow} title={c.title} lead={c.lead} />
      <section className="sas-section" data-testid="privacy-body">
        <div className="sas-container max-w-3xl">
          <p className="text-[12.5px] text-[color:var(--sas-ink-2)] mb-10">
            {c.updatedLabel}: <span className="font-medium text-[color:var(--sas-ink)]">{c.updatedDate}</span>
          </p>
          <div className="flex flex-col gap-10">
            {c.sections.map((s) => (
              <div key={s.heading}>
                <h2 className="sas-h3" data-testid="privacy-section-heading">{s.heading}</h2>
                <div className="mt-3 flex flex-col gap-3 sas-body text-[15.5px] leading-[2]">
                  {s.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="sas-section-sm bg-[color:var(--sas-accent-soft)]/40 border-y border-[color:var(--sas-line)]" data-testid="privacy-contact">
        <div className="sas-container max-w-3xl">
          <div className="sas-eyebrow mb-3">{c.contactEyebrow}</div>
          <p className="sas-body text-[15px]">
            {c.contactBody}
            <LocalizedLink to="/contact/general" className="sas-btn-link underline underline-offset-4" data-testid="privacy-contact-link">
              {c.contactLink}
            </LocalizedLink>
            {c.contactAfter}
          </p>
        </div>
      </section>
    </>
  );
}
